<template>
  <section id="dashboard-analytics">
    <!-- Page title -->
    <div class="w-100 d-flex flex-column flex-md-row justify-content-between between mb-2">
      <h1 class="text-secondary">
        Dashboard
      </h1>
      <div class="d-none d-md-block">
        <b-breadcrumb>
          <b-breadcrumb-item active>
            <feather-icon
              icon="HomeIcon"
              size="16"
            />
            <span style="padding-left: 4px"> Dashboard </span>
          </b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>

    <div>
      <!-- Table Top -->
      <b-row>
        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          lg="8"
          class="mb-1 mb-md-0"
        >
          <div class="d-flex align-items-center justify-content-start justify-content-md-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block w-100 w-md-75"
              placeholder="Buscar..."
            />
          </div>
        </b-col>

        <!-- Per Page and Export Button -->
        <b-col
          cols="12"
          md="6"
          lg="4"
          class="d-flex align-items-center justify-content-end"
        >
          <div class="d-flex align-items-center w-100">
            <label class="mr-2 mb-0">Items</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="flex-grow-1 mr-2"
            />
            <b-button
              variant="outline-primary"
              @click="dialogExport = true"
            >
              Exportar
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <b-overlay
      :show="loading"
      rounded="sm"
      :variant="$store.state.appConfig.layout.skin === 'dark' ? 'dark' : 'light'"
      :class="loading ? 'mt-4 pt-4' : 'mt-2'"
    >
      <!-- SITE DATA -->
      <app-collapse
        v-for="(site, siteIndex) in filteredData"
        id="site-data"
        :key="siteIndex"
        accordion
        type="margin"
        :class="{
          'mt-2 rounded shadow': site.devices.length > 0,
          'border-2-primary': site.devices.length > 0 && sitesVisible[siteIndex],
          'border-secondary': site.devices.length > 0 && !sitesVisible[siteIndex],
        }"
      >
        <app-collapse-item
          v-if="site.devices.length > 0"
          :title="site.name"
          :is-visible="sitesVisible[siteIndex]"
          class="m-0"
          @visible="toggleVisibleSite(siteIndex, $event)"
        >
          <!-- Header: Site info -->
          <template #header>
            <div class="w-100 d-flex justify-content-between">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="MapPinIcon"
                  size="18"
                />
                <strong class="text-uppercase ml-1"> {{ site.name }} </strong>
              </div>
              <div>
                <b-button
                  :id="'tooltip-alerts-status' + site.name"
                  variant="link"
                  to="/alerts-history/"
                  class="position-relative"
                >
                  <feather-icon
                    badge-classes="bg-danger"
                    class="text-body"
                    icon="WifiOffIcon"
                    size="20"
                  />
                  <b-badge
                    pill
                    variant="danger"
                    class="position-absolute"
                    style="height: 15px; width: 15px; top: -10; right: 10px; display: flex; align-items: center; justify-content: center; font-size: 0.75em;"
                  >
                    {{ site.siteStatusAlerts }}
                  </b-badge>
                </b-button>
                <b-tooltip
                  :target="'tooltip-alerts-status' + site.name"
                  title="Dispositivos fuera de línea"
                  class="cursor-pointer"
                />
                <b-button
                  :id="'tooltip-alerts-user' + site.name"
                  variant="link"
                  to="/alerts-history/"
                  class="position-relative"
                >
                  <feather-icon
                    badge-classes="bg-danger"
                    class="text-body"
                    icon="BellIcon"
                    size="20"
                  />
                  <b-badge
                    pill
                    variant="danger"
                    class="position-absolute"
                    style="height: 15px; width: 15px; top: -10; right: 13px; display: flex; align-items: center; justify-content: center; font-size: 0.75em;"
                  >
                    {{ site.siteUserAlerts }}
                  </b-badge>
                </b-button>
                <b-tooltip
                  :target="'tooltip-alerts-user' + site.name"
                  title="Alertas Activas"
                  class="cursor-pointer"
                />
              </div>
            </div>
          </template>
          <!-- Body: Devices info -->
          <!-- Dispositivos tipo "demo" (OEE) usan toda la fila, otros 3 por fila -->
          <b-row>
            <b-col
              v-for="(devData, deviceIndex) in site.devices"
              :key="deviceIndex"
              cols="12"
              :md="devData.sensorType === 'demo' ? '12' : '6'"
              :xl="devData.sensorType === 'demo' ? '12' : '4'"
              class="mb-3"
            >
              <div class="rounded-lg shadow-lg border border-secondary h-100 border-3">
                <new-device-card
                  :ref="`deviceCard-${siteIndex}-${deviceIndex}`"
                  :device="devData"
                  :loading="loadingData"
                  :is-site-visible="sitesVisible[siteIndex]"
                />
              </div>
            </b-col>
          </b-row>
        </app-collapse-item>
      </app-collapse>

      <div
        v-if="data.length"
        class="m-2"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de {{ dataMeta.of }} items</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalData"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

      <template #overlay>
        <!-- Show loading spinner if loading -->
        <loading-logo
          v-if="loading"
          class="mt-4 mb-4"
        />
      </template>
    </b-overlay>

    <b-modal
      v-model="dialogExport"
      title="Exportar datos"
      hide-footer
    >
      <export-data
        :loading.sync="loadingExport"
        @close="dialogExport = false"
        @export="exportData"
      />
    </b-modal>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BButton,
  BPagination,
  BOverlay,
  BTooltip,
  BBreadcrumb,
  BBreadcrumbItem,
  BBadge,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import exportFromJSON from 'export-from-json'
import ExportData from '@/views/utils/ExportData.vue'
import LoadingLogo from '@/views/miscellaneous/LoadingLogo.vue'
import vSelect from 'vue-select'
import NewDeviceCard from './NewDeviceCard.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormInput,
    BButton,
    BPagination,
    BOverlay,
    BTooltip,
    BBreadcrumb,
    BBreadcrumbItem,
    vSelect,
    ExportData,
    LoadingLogo,
    AppCollapse,
    AppCollapseItem,
    BBadge,
    NewDeviceCard,
  },
  data() {
    return {
      data: [],
      filteredData: [],
      searchQuery: '',
      perPage: 12,
      currentPage: 1,
      dialogExport: false,
      loading: false,
      loadingData: false,
      loadingExport: false,
      perPageOptions: [3, 6, 9, 12, 15, 18, 21, 24, 27, 30],
      sitesVisible: [],
      totalData: 0,
    }
  },
  computed: {
    dataMeta() {
      return {
        from: this.perPage * (this.currentPage - 1) + 1,
        to: this.perPage * (this.currentPage - 1) + this.paginatedData.length,
        of: this.totalData,
      }
    },
    paginatedData() {
      const startIndex = (this.currentPage - 1) * this.perPage
      const endIndex = startIndex + this.perPage
      return this.filteredData.slice(startIndex, endIndex)
    },
  },
  watch: {
    searchQuery(val) {
      this.searchDevices(val)
    },
  },
  mounted() {
    this.getData()
    this.$socket.on('new_data', data => {
      this.loadingData = true
      setTimeout(() => {
        this.updateDataReceived(data)
        this.loadingData = false
      }, 500)
    })
  },
  methods: {
    getData() {
      this.loading = true
      this.$http
        .get('/iot-data/dashboard', {
          params: {
            q: this.searchQuery,
            perPage: this.perPage,
            page: this.currentPage,
            isExport: false,
            sortBy: 'name',
          },
        })
        .then(response => {
          this.data = response.data.rows

          // DUMMY DATA FOR OEE DEMO
          const maquinasSite = {
            name: 'Maquinas',
            address: 'Sitio de prueba',
            siteStatusAlerts: 0,
            siteUserAlerts: 0,
            devices: [
              {
                id: 60,
                name: 'Maquina 2',
                device: 'Nodo-consumo-3',
                cloudId: 'acrel-adw300-v3-3',
                timeInterval: 3600,
                sensorType: 'demo',
                color: '#ff0a0a',
                icon: 'ActivityIcon',
                data: 'voltage_in_a',
                values: 5,
                valueBool: false,
                list: { list: [] },
                unitType: 'V',
                createdAt: '2025-10-22T16:51:31.893Z',
                alertCounts: { statusAlerts: 0, userAlerts: 0 },
              },
              {
                id: 61,
                name: 'Maquina 3-24h',
                device: 'Nodo-consumo-24',
                cloudId: 'acrel-adw300-v3-4',
                timeInterval: 3600,
                sensorType: 'demo',
                color: '#8bdd27',
                icon: 'ActivityIcon',
                data: 'voltage_in_a',
                values: 20,
                valueBool: false,
                list: { list: [] },
                unitType: 'V',
                createdAt: '2025-10-22T17:04:33.243Z',
                alertCounts: { statusAlerts: 0, userAlerts: 0 },
              },
            ],
          }

          this.data = [...this.data, maquinasSite]

          this.filteredData = JSON.parse(JSON.stringify(this.data))
          this.totalData = response.data.count
          this.sitesVisible = this.data.map(() => false)
          this.loading = false
        })
        .catch(error => {
          this.$toast.error('Error al obtener los datos')
          console.error('error', error)
          this.loading = false
        })
    },
    exportData() {
      this.loadingExport = true
      try {
        const fileName = 'Reporte de Dispositivos'
        const exportType = exportFromJSON.types.xls
        const data = this.filteredData.map(site => site.devices.map(device => ({
          sitio: site.name,
          direccion: site.address,
          dispositivo: device.name,
          modelo: device.device,
          cloudId: device.cloudId,
          intervalo: device.timeInterval,
          tipo_sensor: device.sensorType,
          color: device.color,
          icono: device.icon,
          dato: device.data,
          valores: device.values,
          unidad: device.unitType,
          creado: this.$moment(device.createdAt).format('DD/MM/YYYY HH:mm:ss'),
          alertas_estado: device.alertCounts.statusAlerts,
          alertas_usuario: device.alertCounts.userAlerts,
          estado_sitio: site.siteStatusAlerts,
          alertas_usuario_sitio: site.siteUserAlerts,
        }))).flat()
        if (data) exportFromJSON({ data, fileName, exportType })
      } catch (error) {
        console.log('error', error)
      }
      this.loadingExport = false
      this.dialogExport = false
    },
    updateDataReceived(data) {
      const updatedData = this.data.map(site => {
        const updatedDevices = site.devices.map(device => {
          if (device.cloudId === data.device) {
            device.createdAt = data.createdAt
            const dataKeys = device.data ? device.data.split(',') : []
            if (dataKeys.length > 1) {
              device.values = dataKeys.map(key => data.data[key])
            } else {
              dataKeys.forEach(key => {
                if (data.data.hasOwnProperty(key)) {
                  if (device.values !== data.data[key] && device.sensorType === 'boolean') {
                    device.valueBool = !device.valueBool;
                  }
                  device.values = data.data[key]          
                }
              })
            }
          }
          return device
        })
        return {
          ...site,
          devices: updatedDevices,
        }
      })

      this.data = updatedData

      if (this.searchQuery) {
        this.searchDevices(this.searchQuery)
      } else {
        this.filteredData = updatedData
      }
    },
    toggleVisibleSite(siteIndex, isVisible) {
      this.$set(this.sitesVisible, siteIndex, isVisible)

      // fix charts when collapse opens
      if (isVisible) {
        this.$nextTick(() => {
          const site = this.filteredData[siteIndex]
          if (site && site.devices) {
            site.devices.forEach((device, deviceIndex) => {
              if (device.sensorType === 'demo') {
                const refName = `deviceCard-${siteIndex}-${deviceIndex}`
                const cardRef = this.$refs[refName]
                if (cardRef && cardRef[0] && cardRef[0].refreshCharts) {
                  cardRef[0].refreshCharts()
                }
              }
            })
          }
        })
      }
    },
    close() {
      this.$nextTick(() => {
        this.editedItem = {}
      })
    },
    searchDevices(searchText) {
      const lowerSearchText = searchText.toLowerCase()

      this.filteredData = this.data.map(site => {
        const siteMatch = Object.keys(site).some(key => {
          if (typeof site[key] === 'string') {
            return site[key].toLowerCase().includes(lowerSearchText)
          }
          return false
        })

        const filteredDevices = site.devices.filter(device => Object.keys(device).some(key => {
          if (typeof device[key] === 'string') {
            return device[key].toLowerCase().includes(lowerSearchText)
          }
          return false
        }))

        if (siteMatch || filteredDevices.length > 0) {
          return {
            ...site,
            devices: filteredDevices.length > 0 ? filteredDevices : site.devices,
          }
        }

        return null
      }).filter(site => site !== null)
    },
  },
}
</script>
